import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  Auth0ProviderOptions,
  GetTokenSilentlyOptions,
} from '@auth0/auth0-react';
import {
  AppState,
  Auth0Context,
  Auth0ContextInterface,
  Auth0Provider,
  User,
  useAuth0,
} from '@auth0/auth0-react';
import { GenericError } from '@auth0/auth0-spa-js';

import { Region } from '../../../utils';
import { useNavigate } from '../../routing';
import {
  getExpirationEpochFromToken,
  getOrgNameFromToken,
  getRegionFromTokenOrDefault,
} from './jwt';
import { useOrgId } from './useOrgId';

export type AuthOptions = Auth0ProviderOptions;
export type TokenOptions = GetTokenSilentlyOptions;

export const createAuthOptions = (opts: AuthOptions) => opts;
export const createTokenOptions = (opts: TokenOptions) => opts;

export const DOMAIN = 'auth.confidence.dev';
export const CLIENT_ID = '2fG3H4RhlAbIZm9Rfn32zTaILH7w1X4w';
export const AUDIENCE = 'https://confidence.dev/';

export const CONFIDENCE_TokenOptions = createTokenOptions({
  authorizationParams: {
    audience: AUDIENCE,
    scope: 'openid profile email offline_access',
  },
});

export const CONFIDENCE_DefaultAuthOptions: Omit<AuthOptions, 'clientId'> = {
  domain: DOMAIN,
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  useRefreshTokensFallback: true, // Temporary, remove once affected users have logged in again
  ...CONFIDENCE_TokenOptions,
};

// Hour of day when, if possible, to refresh the access token
const DESIRED_TOKEN_REFRESH_HOUR = 3;

const TokenClaimsContext = createContext<{ orgName?: string }>({});

const TokenWrapper = ({
  context,
  onTokenReceived,
  children,
}: PropsWithChildren<{
  onTokenReceived: (token: string, region: Region) => void;
  context: Auth0ContextInterface<User>;
}>) => {
  const [token, setToken] = useState<string | null>(null);

  const getToken = useCallback(
    async (options = CONFIDENCE_TokenOptions) => {
      if (!context.isAuthenticated) return;
      try {
        const tkn = await context.getAccessTokenSilently({
          ...options,
          authorizationParams: {
            ...options.authorizationParams,
            organization: context?.user?.org_id,
          },
        });

        onTokenReceived(tkn, getRegionFromTokenOrDefault(tkn));
        setToken(tkn);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);

        if (
          ['login_required', 'invalid_grant', 'missing_refresh_token'].includes(
            (e as GenericError).error,
          )
        ) {
          context.loginWithRedirect({
            authorizationParams: { organization: context.user?.org_id },
          });
        }
      }
    },
    [context, onTokenReceived],
  );

  useEffect(() => {
    getToken();
  }, [context.isAuthenticated, context?.user?.org_id]);

  useEffect(() => {
    /*
     * The goal of this hook is to make sure token refresh happens when it's least likely a user is in the
     * middle of performing work in the app. This is because when the token is refreshed, the Apollo client
     * is recreated, resulting in a full reload of the react tree. When this happens, any unsaved data is lost.
     *
     * The way this works is by trying to figure out a time of day when the user is unlikely to be working,
     * and then refresh the token at that time. There are 7 different combinations of current time and expiry
     * time of the access token.
     */
    if (!token) {
      return undefined;
    }

    const tokenExpirationEpoch = getExpirationEpochFromToken(token);
    if (!tokenExpirationEpoch) {
      return undefined;
    }

    const tokenExpirationDate = new Date(tokenExpirationEpoch);
    const now = new Date();

    const preliminaryRefreshDate = new Date(tokenExpirationDate);
    preliminaryRefreshDate.setHours(DESIRED_TOKEN_REFRESH_HOUR);
    preliminaryRefreshDate.setMinutes(0);
    preliminaryRefreshDate.setSeconds(0);
    preliminaryRefreshDate.setMilliseconds(0);

    let refreshEpoch: number;
    if (
      preliminaryRefreshDate <= tokenExpirationDate &&
      preliminaryRefreshDate >= now
    ) {
      // Okay to refresh on the preliminaryRefreshDate since it's after now but before the token expires
      refreshEpoch = preliminaryRefreshDate.getTime();
    } else if (
      preliminaryRefreshDate > tokenExpirationDate &&
      tokenExpirationDate.getDate() !== now.getDate() &&
      now.getHours() < DESIRED_TOKEN_REFRESH_HOUR
    ) {
      // Handles an edge case where the token expires the next day, but before the desired refresh hour,
      // AND the current hour is before the desired refresh hour. In this case, we should refresh the token
      // at the desired hour the CURRENT day. If we were to take the branch below, we would refresh the token
      // the same time every day (between 00 and DESIRED_TOKEN_REFRESH_HOUR).
      const upcomingRefreshDate = new Date();
      upcomingRefreshDate.setHours(DESIRED_TOKEN_REFRESH_HOUR);
      upcomingRefreshDate.setMinutes(0);
      upcomingRefreshDate.setSeconds(0);
      upcomingRefreshDate.setMilliseconds(0);
      refreshEpoch = upcomingRefreshDate.getTime();
    } else {
      // Refresh the token immediately. This will lead to the token being refreshed at the desired hour,
      // sometimes the next day, sometimes in two days.
      refreshEpoch = 0;
    }

    /*
     * Use setInterval to check regularly if the desired refresh time has passed. If so, refresh the token.
     *
     * The reason we don't use setTimeout is that behavior varies depending on both browser and OS when the
     * computer goes to sleep or hibernate. Using setInterval and polling more frequently is more reliable.
     * The relatively high frequency of the polling is to avoid the user coming back and resuming work with
     * an expired token.
     */
    const CHECK_TIME_INTERVAL = 1000 * 10;
    const checkInterval = setInterval(() => {
      if (Date.now() > refreshEpoch) {
        getToken({
          ...CONFIDENCE_TokenOptions,
          cacheMode: 'off', // Force refresh from server
        });
        // Stop polling to avoid multiple parallel refreshes
        clearInterval(checkInterval);
      }
    }, CHECK_TIME_INTERVAL);

    return () => {
      clearInterval(checkInterval);
    };
  }, [token, getToken]);

  const tokenClaims = useMemo(
    () => ({ orgName: token ? getOrgNameFromToken(token) : undefined }),
    [token],
  );

  return (
    <TokenClaimsContext.Provider value={tokenClaims}>
      {children}
    </TokenClaimsContext.Provider>
  );
};

/**
 * ! Constraint #02 - No Hardwired Config.
 * ! The Provider comes with backed-in `Confidence_AuthOptions`
 *
 * * todo(@oussaz) - passe AuthOptions as a prop or renderProp.
 */
export const AuthenticationProvider = ({
  children,
  onTokenReceived,
  clientId = CLIENT_ID,
  redirectUri,
}: {
  children: ReactElement;
  onTokenReceived: (token: string, region: Region) => void;
  clientId?: string;
  redirectUri?: string;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [storedOrgId, setStoredOrgId] = useOrgId();
  const hasInvitation = !!searchParams.get('invitation');
  const onRedirectCallback = (appState?: AppState, user?: User) => {
    if (user && user.org_id) {
      setStoredOrgId(user.org_id);
    }
    navigate(appState?.returnTo || window.location.pathname);
  };

  const orgId =
    searchParams.get('organization') ||
    searchParams.get('account') ||
    storedOrgId ||
    undefined;

  const authOptions = useMemo(
    () =>
      createAuthOptions({
        ...CONFIDENCE_DefaultAuthOptions,
        clientId,
        skipRedirectCallback:
          window.location.pathname === '/admin/notifications',
        authorizationParams: {
          ...CONFIDENCE_DefaultAuthOptions.authorizationParams,
          organization: orgId,
        },
      }),
    [clientId, orgId],
  );

  return (
    <Auth0Provider
      {...authOptions}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        ...authOptions.authorizationParams,
        redirect_uri: redirectUri ?? window.location.origin,
        ...(hasInvitation
          ? {
              invitation: searchParams.get('invitation')!,
              organization: searchParams.get('organization')!,
              organization_name: searchParams.get('organization_name')!,
            }
          : { organization: orgId }),
      }}
    >
      <Auth0Context.Consumer>
        {value => (
          <TokenWrapper context={value} onTokenReceived={onTokenReceived}>
            {children}
          </TokenWrapper>
        )}
      </Auth0Context.Consumer>
    </Auth0Provider>
  );
};

export const useAuth = () => {
  const {
    loginWithPopup: auth0LoginWithPopup,
    loginWithRedirect: auth0LoginWithRedirect,
    logout: auth0Logout,
    error: auth0Error,
    ...auth0
  } = useAuth0();
  const [_orgId, setOrgId] = useOrgId();
  const { orgName } = useContext(TokenClaimsContext);

  // Filter out "popup closed" errors - closing the popup is a valid user action
  // (e.g., to click Sign up instead), not an error that should be displayed.
  const error =
    auth0Error && (auth0Error as GenericError).error === 'cancelled'
      ? undefined
      : auth0Error;

  const loginWithPopup: typeof auth0LoginWithPopup = useCallback(
    (options, config) => {
      if (options?.authorizationParams?.organization) {
        setOrgId(options.authorizationParams.organization);
      }
      return auth0LoginWithPopup(options, config);
    },
    [auth0LoginWithPopup],
  );

  const loginWithRedirect: typeof auth0LoginWithRedirect = useCallback(
    options => {
      if (options?.authorizationParams?.organization) {
        setOrgId(options.authorizationParams.organization);
      }
      return auth0LoginWithRedirect(options);
    },
    [auth0LoginWithRedirect],
  );

  const logout: typeof auth0Logout = useCallback(
    options => {
      // Reset orgId to enable login with a freetext organization
      setOrgId(null);
      return auth0Logout(options);
    },
    [auth0Logout],
  );

  return {
    ...auth0,
    error,
    loginWithPopup,
    loginWithRedirect,
    logout,
    orgName,
  };
};
